'use client';

import React, { createContext, use, useState, ReactNode } from 'react';
import { useProfileQuery } from '@/lib/services/profile/hooks';

const OVERRIDES_STORAGE_KEY = 'vale-feature-flag-overrides';

type FeatureFlagOverrides = Record<string, boolean>;

interface FeatureFlagsContextType {
  features: string[];
  hasFeature: (featureKey: string) => boolean;
  isLoading: boolean;
  overrides: FeatureFlagOverrides;
  setFeatureOverride: (flag: string, enabled: boolean | null) => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined
);

/**
 * Provider for feature flags from backend
 * Fetches features from /profile endpoint and provides global access
 */
function loadOverrides(): FeatureFlagOverrides {
  if (typeof window === 'undefined') return {};
  try {
    const stored = localStorage.getItem(OVERRIDES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] =
    useState<FeatureFlagOverrides>(loadOverrides);

  // Subscribe to the canonical profile query so the dashboard mount
  // shares one `/profile` fetch with `UserProfileProvider` /
  // `useUserProfile` consumers instead of triggering its own.
  const { data, isLoading } = useProfileQuery();

  // Extract features from profile response, then apply overrides
  const features = React.useMemo(() => {
    const baseFeatures: string[] =
      data && 'features' in data ? data.features : [];

    const featureSet = new Set(baseFeatures);
    for (const [flag, enabled] of Object.entries(overrides)) {
      if (enabled) {
        featureSet.add(flag);
      } else {
        featureSet.delete(flag);
      }
    }
    return Array.from(featureSet);
  }, [data, overrides]);

  const hasFeature = React.useCallback(
    (featureKey: string) => {
      return features.includes(featureKey);
    },
    [features]
  );

  const setFeatureOverride = React.useCallback(
    (flag: string, enabled: boolean | null) => {
      setOverrides((prev) => {
        const next = { ...prev };
        if (enabled === null) {
          delete next[flag];
        } else {
          next[flag] = enabled;
        }
        try {
          if (Object.keys(next).length === 0) {
            localStorage.removeItem(OVERRIDES_STORAGE_KEY);
          } else {
            localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(next));
          }
        } catch {
          // localStorage unavailable
        }
        return next;
      });
    },
    []
  );

  const value = React.useMemo<FeatureFlagsContextType>(
    () => ({
      features,
      hasFeature,
      isLoading,
      overrides,
      setFeatureOverride
    }),
    [features, hasFeature, isLoading, overrides, setFeatureOverride]
  );

  return <FeatureFlagsContext value={value}>{children}</FeatureFlagsContext>;
}

/**
 * Hook to access feature flags from anywhere in the app
 *
 * @example
 * ```tsx
 * const { hasFeature, features } = useFeatureFlags();
 *
 * if (hasFeature('global-documents')) {
 *   return <DocumentsTab />;
 * }
 * ```
 */
export function useFeatureFlags() {
  const context = use(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider'
    );
  }
  return context;
}

/**
 * Optional hook for components that don't require the provider
 * Returns empty features if provider is not available
 */
export function useOptionalFeatureFlags() {
  try {
    return useFeatureFlags();
  } catch {
    return {
      features: [],
      hasFeature: () => false,
      isLoading: false,
      overrides: {},
      setFeatureOverride: () => {}
    };
  }
}
